import { animated, useSpring } from '@react-spring/web';
import { useTranslation } from 'next-i18next';
import { useEffect, useRef, useState } from 'react';

import { updateVersionState } from '@/api/common';

const Hint = ({
  visible,
  setVisible,
  buttonRef,
}: {
  visible: boolean;
  setVisible: (visible: boolean) => void;
  buttonRef: React.RefObject<HTMLDivElement>;
}) => {
  const { t } = useTranslation(['common']);
  const [position, setPosition] = useState({ left: 0, top: 61 });
  const hintRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (visible && buttonRef?.current) {
      const updatePosition = () => {
        const buttonRect = buttonRef.current?.getBoundingClientRect();
        const headerElement = document.getElementById('home-layout-header');
        const headerRect = headerElement?.getBoundingClientRect();

        if (buttonRect && headerRect) {
          // 计算切换按钮中心相对于header的位置
          const buttonCenterX =
            buttonRect.left - headerRect.left + buttonRect.width / 2;

          // 获取提示框的实际宽度
          const hintWidth = hintRef.current?.offsetWidth || 150;

          // 让提示框的中心与按钮中心对齐
          const left = buttonCenterX - hintWidth / 2;
          const top = buttonRect.bottom - headerRect.top + 4;

          setPosition({ left, top });
        }
      };

      // 使用 requestAnimationFrame 确保在下一帧渲染后执行
      const rafId = requestAnimationFrame(() => {
        // 再次使用 requestAnimationFrame 确保 DOM 更新完成
        requestAnimationFrame(updatePosition);
      });

      // 使用 ResizeObserver 监听提示框尺寸变化
      let resizeObserver: ResizeObserver | null = null;
      if (hintRef.current) {
        resizeObserver = new ResizeObserver(() => {
          updatePosition();
        });
        resizeObserver.observe(hintRef.current);
      }

      // 监听窗口大小变化和滚动事件
      window.addEventListener('resize', updatePosition);
      window.addEventListener('scroll', updatePosition);

      return () => {
        cancelAnimationFrame(rafId);
        if (resizeObserver) {
          resizeObserver.disconnect();
        }
        window.removeEventListener('resize', updatePosition);
        window.removeEventListener('scroll', updatePosition);
      };
    }
  }, [visible, buttonRef]);

  const styles = useSpring({
    opacity: visible ? 1 : 0,
    display: visible ? 'flex' : 'none',
    boxShadow: 'rgba(0, 0, 0, 0.08) 0px 2px 20px 0px',
    delay: key => (key === 'display' ? 150 : 0),
  });

  return (
    <animated.div
      ref={hintRef}
      className="absolute z-[99] box-border flex items-center rounded-[12px] border-[1px] border-solid border-[rgba(0,0,0,0.06)] bg-white p-[12px] pr-[36px] text-[14px] leading-[24px]"
      style={{ ...styles, left: position.left, top: position.top + 16 }}>
      <svg
        className="absolute left-1/2 top-[-8px] translate-x-[-50%]"
        width="75"
        height="8"
        viewBox="0 0 75 8"
        fill="none"
        xmlns="http://www.w3.org/2000/svg">
        <g clip-path="url(#clip0_7734_34837)">
          <path
            d="M39.9962 2.74423C38.8087 0.963028 36.1913 0.963027 35.0038 2.74423L31.5 8L43.5 8L39.9962 2.74423Z"
            fill="white"
          />
          <path
            d="M39.9962 2.74423C38.8087 0.963028 36.1913 0.963027 35.0038 2.74423L31.5 8L43.5 8L39.9962 2.74423Z"
            fill="white"
          />
          <path
            d="M39.5801 3.02148C38.5905 1.53727 36.4095 1.53727 35.4199 3.02148L32.4346 7.5L42.5654 7.5L39.5801 3.02148Z"
            stroke="black"
            stroke-opacity="0.06"
          />
          <path
            d="M42.3 8L39.164 3.29883C38.3724 2.11148 36.6275 2.11148 35.8359 3.29883L32.7 8H42.3Z"
            fill="white"
          />
        </g>
        <defs>
          <clipPath id="clip0_7734_34837">
            <rect
              width="74"
              height="8"
              fill="white"
              transform="matrix(1 0 0 -1 0.5 8)"
            />
          </clipPath>
        </defs>
      </svg>
      <svg
        className="h-[32px] w-[32px]"
        xmlns="http://www.w3.org/2000/svg"
        width="32"
        height="32"
        viewBox="0 0 32 32"
        fill="none">
        <g clip-path="url(#clip0_7734_37750)">
          <path
            d="M29.9375 30.8188C30 30.5376 30.0312 30.2438 30.0312 29.9438C30.0312 27.7501 28.2563 25.9751 26.0625 25.9751C24.175 25.9751 22.5875 27.2938 22.1875 29.0626C21.4688 28.3251 20.4625 27.8688 19.35 27.8688C17.9375 27.8688 16.7 28.6063 15.9937 29.7126C15.2875 28.6063 14.05 27.8688 12.6375 27.8688C11.525 27.8688 10.5187 28.3251 9.8 29.0626C9.4 27.2938 7.81875 25.9751 5.925 25.9751C3.73125 25.9751 1.95625 27.7501 1.95625 29.9438C1.95625 30.2438 1.9875 30.5376 2.05 30.8188"
            fill="#FEF3E2"
          />
          <path
            d="M31.375 31.3691H18.6H11.1062H0.625"
            stroke="#FEF3E2"
            stroke-miterlimit="10"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
          <path
            d="M20.05 5.1001C21.1687 7.0126 21.6875 8.95635 21.6437 11.1313C21.6 13.6751 20.625 16.8376 19.8062 19.3126H12.2875C11.4687 16.8376 10.4812 13.6688 10.45 11.1313C10.4062 8.95635 10.925 7.0126 12.0437 5.1001"
            fill="#FFB624"
          />
          <path
            d="M16.05 19.3125H12.2812C12.5125 19.8213 12.75 20.3212 12.9688 20.7985C13.05 20.9831 13.2875 21.1047 13.5562 21.1092H18.525C18.8 21.1047 19.0375 20.9831 19.1125 20.7985C19.3312 20.3212 19.5687 19.8213 19.8 19.3125H16.05Z"
            fill="#FFB624"
          />
          <path
            d="M13.6687 21.8003C13.4812 22.094 13.375 22.4315 13.375 22.7878C13.375 25.1128 16 26.2003 16 26.2003C16 26.2003 18.625 25.1128 18.625 22.7878C18.625 22.4315 18.5187 22.1003 18.3312 21.8003H13.6687Z"
            fill="#FFB624"
          />
          <path
            d="M15.1119 21.8003C15.0405 21.9338 15 22.0872 15 22.2492C15 23.306 16 23.8003 16 23.8003C16 23.8003 17 23.306 17 22.2492C17 22.0872 16.9595 21.9367 16.8881 21.8003H15.1119Z"
            fill="#FFD28D"
          />
          <path
            d="M12.2812 19.3128C11.7562 17.719 11.1562 15.8378 10.7875 14.019L10.4625 13.969L9.8375 14.594C6.7125 17.719 9.61875 22.6003 9.625 22.769C9.675 22.9128 11.4687 20.994 12.475 19.9003C12.4125 19.7065 12.3437 19.5065 12.2812 19.3128ZM22.1687 14.594L21.3437 13.769C20.9812 15.6628 20.3562 17.644 19.8062 19.3128C19.7375 19.5253 19.6687 19.7315 19.6 19.9378C20.5687 21.0878 22.275 23.0878 22.3812 22.769C22.3875 22.6003 25.275 17.7003 22.1687 14.594Z"
            fill="#E53838"
          />
          <path
            d="M20.05 5.1002C19.9812 5.16895 19.7687 5.3627 19.7 5.4377C18.6687 6.43145 17.3687 6.9252 16.0437 6.9252C14.7187 6.9252 13.4187 6.43145 12.3875 5.4377C12.3187 5.36895 12.1062 5.1752 12.0375 5.1002C12.9625 3.5252 14.2812 2.04395 15.6187 0.812695C15.7312 0.700195 15.8812 0.637695 16.0437 0.637695C16.2 0.637695 16.35 0.700195 16.4687 0.812695C17.8062 2.04395 19.1125 3.53145 20.05 5.1002Z"
            fill="#FFB624"
          />
          <mask
            id="mask0_7734_37750"
            maskUnits="userSpaceOnUse"
            x="12"
            y="0"
            width="9"
            height="7">
            <path
              d="M20.05 5.1002C19.9812 5.16895 19.7687 5.3627 19.7 5.4377C18.6687 6.43145 17.3687 6.9252 16.0437 6.9252C14.7187 6.9252 13.4187 6.43145 12.3875 5.4377C12.3187 5.36895 12.1062 5.1752 12.0375 5.1002C12.9625 3.5252 14.2812 2.04395 15.6187 0.812695C15.7312 0.700195 15.8812 0.637695 16.0437 0.637695C16.2 0.637695 16.35 0.700195 16.4687 0.812695C17.8062 2.04395 19.1125 3.53145 20.05 5.1002Z"
              fill="#F79B0F"
            />
          </mask>
          <g mask="url(#mask0_7734_37750)">
            <path
              d="M20.05 3.1002C19.9812 3.16895 19.7687 3.3627 19.7 3.4377C18.6687 4.43145 17.3687 4.9252 16.0437 4.9252C14.7187 4.9252 13.4187 4.43145 12.3875 3.4377C12.3187 3.36895 12.1062 3.1752 12.0375 3.1002C12.9625 1.5252 14.2812 0.0439453 15.6187 -1.1873C15.7312 -1.2998 15.8812 -1.3623 16.0437 -1.3623C16.2 -1.3623 16.35 -1.2998 16.4687 -1.1873C17.8062 0.0439453 19.1125 1.53145 20.05 3.1002Z"
              fill="#E53838"
            />
          </g>
          <path
            d="M16.0441 9.56885C17.2659 9.56906 18.2561 10.5599 18.2561 11.7817C18.2558 13.0034 17.2658 13.9934 16.0441 13.9937C14.8223 13.9937 13.8315 13.0035 13.8313 11.7817C13.8313 10.5598 14.8222 9.56885 16.0441 9.56885Z"
            fill="#4DA7F7"
            stroke="white"
          />
        </g>
        <defs>
          <clipPath id="clip0_7734_37750">
            <rect width="32" height="32" fill="white" />
          </clipPath>
        </defs>
      </svg>
      <div className="ml-[12px]">
        <p className="leading-[140%] text-[#CD292A]">
          {t('版本切换-浮窗-标题')}
        </p>
        <p className="leading-[140%]">{t('版本切换-浮窗-描述')}</p>
      </div>
      <svg
        onClick={() => {
          updateVersionState({ events: [{ type: 'WEB_FLOAT_NOTICE' }] });
          setVisible(false);
        }}
        className="absolute right-[12px] top-1/2 translate-y-[-50%] cursor-pointer"
        width="16"
        height="16"
        viewBox="0 0 16 16"
        fill="none"
        xmlns="http://www.w3.org/2000/svg">
        <g opacity="0.45">
          <path
            d="M4.66666 4.6665L11.3333 11.3332"
            stroke="#0A0A0A"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
          <path
            d="M4.66666 11.3332L11.3333 4.6665"
            stroke="#0A0A0A"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </g>
      </svg>
    </animated.div>
  );
};

export default Hint;
